import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../App';
import '../styles/NieuweBestellingPage.css';
import '../styles/BestellerMobile.css';
import AndersProductenPage from './AndersProductenPage';
import FeedbackModal from '../components/FeedbackModal';
import PageLoader from '../components/PageLoader';
import ProductZoekenLijst from '../components/ProductZoekenLijst';
import BestellingDatumOverzicht from '../components/BestellingDatumOverzicht';
import { useProductZoeken } from '../hooks/useProductZoeken';
import {
  loadDraft,
  saveDraft,
  clearDraft,
  hasDraftContent,
  formatDraftSavedAt
} from '../utils/orderDraft';

function NieuweBestellingPage({ user, onBack, onLogout }) {
  const [productgroepen, setProductgroepen] = useState([]);
  const [groepenProducten, setGroepenProducten] = useState({});
  const [activeGroepId, setActiveGroepId] = useState(null);
  const [leverdatum, setLeverdatum] = useState('');
  const [datums, setDatums] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [vrijeRegels, setVrijeRegels] = useState([]);
  const [showAndersPage, setShowAndersPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [notitie, setNotitie] = useState('');
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [showRestoreDraft, setShowRestoreDraft] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [bestaandeBestellingen, setBestaandeBestellingen] = useState([]);
  const [loadingBestaand, setLoadingBestaand] = useState(false);

  const productenPanelRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const speltakId = user?.speltak_id;

  const { query: zoekQuery, setQuery: setZoekQuery, results: zoekResultaten, loading: zoekLaden, isSearching } = useProductZoeken();
  const isAndersGroep = (groep) => groep.naam.toLowerCase() === 'anders';
  const andersGroep = productgroepen.find(isAndersGroep);

  const getDraftPayload = useCallback(() => ({
    leverdatum,
    quantities,
    vrijeRegels,
    notitie
  }), [leverdatum, quantities, vrijeRegels, notitie]);

  const persistDraft = useCallback((showFeedback = true) => {
    const saved = saveDraft(speltakId, getDraftPayload());
    if (saved) {
      setDraftSavedAt(saved.savedAt);
      if (showFeedback) return true;
    } else {
      setDraftSavedAt(null);
    }
    return !!saved;
  }, [speltakId, getDraftPayload]);

  const isFormDirty = () => hasDraftContent(getDraftPayload());

  const countCartItems = () => {
    const productCount = Object.values(quantities).filter((qty) => parseInt(qty, 10) > 0).length;
    return productCount + vrijeRegels.length;
  };

  const applyDraft = (draft) => {
    setLeverdatum(draft.leverdatum || '');
    setQuantities(draft.quantities || {});
    setVrijeRegels(draft.vrijeRegels || []);
    setNotitie(draft.notitie || '');
    setDraftSavedAt(draft.savedAt || null);
  };

  const handleBackClick = () => {
    if (isFormDirty() && !successMessage) {
      setConfirmLeave(true);
      return;
    }
    onBack();
  };

  const loadBestaandeVoorDatum = useCallback(async (datumId) => {
    if (!datumId) {
      setBestaandeBestellingen([]);
      return;
    }

    try {
      setLoadingBestaand(true);
      const response = await api.get(`/index.php?action=mybestellingoverzicht&datum_id=${datumId}`);
      setBestaandeBestellingen(response.data);
      setLoadingBestaand(false);
    } catch {
      setBestaandeBestellingen([]);
      setLoadingBestaand(false);
    }
  }, []);

  useEffect(() => {
    loadProductgroepen();
    loadDatums();
  }, []);

  useEffect(() => {
    if (loading || !speltakId) return;

    const draft = loadDraft(speltakId);
    if (draft) {
      setPendingDraft(draft);
      setShowRestoreDraft(true);
    }
  }, [loading, speltakId]);

  useEffect(() => {
    if (!speltakId || successMessage) return undefined;

    if (draftSaveTimerRef.current) {
      clearTimeout(draftSaveTimerRef.current);
    }

    if (!hasDraftContent({ leverdatum, quantities, vrijeRegels, notitie })) {
      return undefined;
    }

    draftSaveTimerRef.current = setTimeout(() => {
      persistDraft(false);
    }, 1500);

    return () => {
      if (draftSaveTimerRef.current) {
        clearTimeout(draftSaveTimerRef.current);
      }
    };
  }, [leverdatum, quantities, vrijeRegels, notitie, speltakId, successMessage, persistDraft]);

  useEffect(() => {
    loadBestaandeVoorDatum(leverdatum);
  }, [leverdatum, loadBestaandeVoorDatum]);

  const loadDatums = async () => {
    try {
      const response = await api.get('/index.php?action=datums');
      setDatums(response.data);
    } catch (error) {
      console.error('Error loading datums:', error);
    }
  };

  const loadProductgroepen = async () => {
    try {
      const response = await api.get('/index.php?action=productgroepen');
      setProductgroepen(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading product groups:', error);
      setLoading(false);
    }
  };

  const loadProducten = async (groepId) => {
    try {
      const response = await api.get(`/index.php?action=producten&groep=${groepId}`);
      setGroepenProducten((prev) => ({
        ...prev,
        [groepId]: response.data
      }));
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const handleGroepClick = (groep) => {
    if (isAndersGroep(groep)) {
      setShowAndersPage(true);
      return;
    }

    const groepId = groep.ID;

    if (activeGroepId === groepId) {
      setActiveGroepId(null);
      return;
    }

    setActiveGroepId(groepId);

    if (!groepenProducten[groepId]) {
      loadProducten(groepId);
    }

    requestAnimationFrame(() => {
      productenPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const handleQuantityChange = (productId, value) => {
    setQuantities({
      ...quantities,
      [productId]: value === '' ? '' : parseInt(value) || 0
    });
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const handleSaveDraftClick = () => {
    if (!isFormDirty()) {
      setSubmitError('Er is nog niets om als concept op te slaan.');
      return;
    }
    persistDraft(true);
    setSubmitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!leverdatum) {
      setSubmitError('Selecteer alstublieft een leveringsdatum');
      return;
    }

    const orderedProducts = Object.entries(quantities)
      .filter(([_, qty]) => parseInt(qty, 10) > 0)
      .reduce((acc, [prodid, qty]) => {
        acc[prodid] = qty;
        return acc;
      }, {});

    if (Object.keys(orderedProducts).length === 0 && vrijeRegels.length === 0) {
      setSubmitError('Selecteer alstublieft minimaal één product met hoeveelheid groter dan 0');
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);
      setSuccessMessage(null);

      const response = await api.post('/index.php?action=saveorder', {
        leverdatum: leverdatum,
        regels: orderedProducts,
        vrije_regels: vrijeRegels,
        notitie: notitie.trim() || null
      });

      clearDraft(speltakId);
      setDraftSavedAt(null);

      setSuccessMessage(
        `Uw bestelling is geplaatst met bestelnummer ${response.data.bstlid}.`
      );

      setLeverdatum('');
      setActiveGroepId(null);
      setGroepenProducten({});
      setQuantities({});
      setVrijeRegels([]);
      setNotitie('');
      setBestaandeBestellingen([]);
      setSubmitting(false);
      loadBestaandeVoorDatum('');
    } catch (error) {
      console.error('Error saving order:', error);
      const errorMsg = error.response?.data?.message || 'Er is een fout opgetreden bij het opslaan';
      setSubmitError(errorMsg);
      setSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    onBack();
  };

  const cartCount = countCartItems();
  const selectedDatumLabel = datums.find((d) => String(d.ID) === String(leverdatum))?.formatted;

  if (loading) {
    return <PageLoader />;
  }

  if (showAndersPage) {
    return (
      <AndersProductenPage
        vrijeRegels={vrijeRegels}
        onSave={setVrijeRegels}
        onBack={() => setShowAndersPage(false)}
        onLogout={onLogout}
      />
    );
  }

  return (
    <div className="nieuwe-bestelling-container">
      {successMessage && (
        <FeedbackModal
          title="Bestelling geplaatst"
          message={successMessage}
          onConfirm={handleSuccessOk}
          confirmLabel="Ok"
        />
      )}

      {showRestoreDraft && pendingDraft && (
        <FeedbackModal
          title="Concept gevonden"
          message={`Je hebt een opgeslagen concept van ${formatDraftSavedAt(pendingDraft.savedAt)}. Wil je dit herstellen?`}
          onConfirm={() => {
            applyDraft(pendingDraft);
            setShowRestoreDraft(false);
            setPendingDraft(null);
          }}
          onCancel={() => {
            setShowRestoreDraft(false);
            setPendingDraft(null);
          }}
          confirmLabel="Herstellen"
          cancelLabel="Nieuw beginnen"
        />
      )}

      {confirmLeave && (
        <FeedbackModal
          title="Bestelling annuleren?"
          message="Je hebt wijzigingen die nog niet zijn geplaatst. Wil je je concept opslaan voordat je teruggaat?"
          onConfirm={() => {
            persistDraft(true);
            setConfirmLeave(false);
            onBack();
          }}
          onCancel={() => setConfirmLeave(false)}
          confirmLabel="Concept opslaan en terug"
          cancelLabel="Blijven"
        />
      )}

      <header className="bestelling-header">
        <button type="button" onClick={handleBackClick} className="back-button">← Terug</button>
        <h1>Nieuwe Bestelling</h1>
        <button onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="bestelling-main bestelling-main--with-sticky">
        <div className="bestelling-form">
          {draftSavedAt && (
            <div className="draft-status-banner" role="status">
              Concept opgeslagen om {formatDraftSavedAt(draftSavedAt)}
            </div>
          )}

          {submitError && (
            <div className="error-message" role="alert">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} id="nieuwe-bestelling-form">
            <input type="hidden" value={user?.inlognaam} />
            <input type="hidden" value={getTodayDate()} />

            <div className="form-section datum-row">
              <p className="besteldatum-display">
                <span className="besteldatum-label">Besteldatum: </span>
                <span className="besteldatum-value">{getTodayFormatted()}</span>
              </p>
              <div className="leverdatum-select-wrap">
                <label htmlFor="leverdatum" className="visually-hidden">
                  Leveringsdatum
                </label>
                <select
                  id="leverdatum"
                  value={leverdatum}
                  onChange={(e) => {
                    setSubmitError(null);
                    setLeverdatum(e.target.value);
                  }}
                  required
                  className="form-input leverdatum-select"
                >
                  <option value="">-- Kies een leveringsdatum --</option>
                  {datums.map((datum) => (
                    <option key={datum.ID} value={datum.ID}>
                      {datum.formatted}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {leverdatum && (
              <div className="form-section bestaand-per-datum-section">
                <label>
                  <strong>Al besteld voor {selectedDatumLabel}</strong>
                </label>
                <BestellingDatumOverzicht
                  groepen={bestaandeBestellingen}
                  loading={loadingBestaand}
                  compact
                  emptyMessage="Je hebt voor deze leveringsdatum nog geen bestelling geplaatst."
                />
              </div>
            )}

            <div className="form-section productgroepen-section">
              <label>
                <strong>Producten bestellen</strong>
              </label>
              <div className="product-zoeken-bar">
                <label htmlFor="product-zoeken">Zoek product</label>
                <input
                  id="product-zoeken"
                  type="search"
                  className="product-zoeken-input"
                  placeholder="Zoek in alle producten..."
                  value={zoekQuery}
                  onChange={(e) => setZoekQuery(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="bestel-product-layout">
                <aside className="productgroepen-panel">
                  <p className="panel-hint">Kies een productgroep</p>
                  <div className="productgroepen-list">
                    {productgroepen.length === 0 ? (
                      <p className="no-groups">Geen productgroepen beschikbaar</p>
                    ) : (
                      productgroepen
                        .filter((groep) => !isAndersGroep(groep))
                        .map((groep) => (
                          <button
                            key={groep.ID}
                            type="button"
                            className={`productgroep-item ${activeGroepId === groep.ID ? 'active' : ''}`}
                            onClick={() => handleGroepClick(groep)}
                            title={groep.beschrijving}
                          >
                            {groep.naam}
                          </button>
                        ))
                    )}
                    {andersGroep && (
                      <button
                        type="button"
                        className={`productgroep-item ${vrijeRegels.length > 0 ? 'active' : ''}`}
                        onClick={() => handleGroepClick(andersGroep)}
                        title="Producten die niet in de standaard lijst staan"
                      >
                        {andersGroep.naam}
                        {vrijeRegels.length > 0 && (
                          <span className="anders-badge"> ({vrijeRegels.length})</span>
                        )}
                      </button>
                    )}
                  </div>
                </aside>

                <div className="producten-panel" ref={productenPanelRef}>
                  {isSearching ? (
                    <div className="producten-panel-content">
                      <h3 className="producten-panel-title">Zoekresultaten</h3>
                      <ProductZoekenLijst
                        results={zoekResultaten}
                        loading={zoekLaden}
                        quantities={quantities}
                        onQuantityChange={handleQuantityChange}
                      />
                    </div>
                  ) : activeGroepId ? (
                    (() => {
                      const groep = productgroepen.find((g) => g.ID === activeGroepId);
                      const producten = groepenProducten[activeGroepId] || [];

                      return (
                        <div className="producten-panel-content">
                          <h3 className="producten-panel-title">Producten in &quot;{groep?.naam}&quot;</h3>
                          {producten.length > 0 ? (
                            <div className="producten-list">
                              {producten.map((product) => (
                                <div key={product.ID} className="product-item">
                                  <label>
                                    {product.product} {product.eenheid && `(${product.eenheid})`}
                                  </label>
                                  <input
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    value={quantities[product.ID] || ''}
                                    onChange={(e) => handleQuantityChange(product.ID, e.target.value)}
                                    placeholder="0"
                                  />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="producten-loading">Producten laden...</p>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <p className="producten-panel-empty">
                      Zoek een product of selecteer een productgroep om hoeveelheden in te vullen.
                    </p>
                  )}
                </div>
              </div>
            </div>

            {vrijeRegels.length > 0 && (
              <div className="form-section">
                <strong>Vrije producten (Anders)</strong>
                <div className="producten-list">
                  {vrijeRegels.map((regel, index) => (
                    <div key={`vrij-${index}`} className="product-item">
                      <label>{regel.omschrijving}</label>
                      <span className="vrije-regel-qty">{regel.hoeveelheid}</span>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  className="edit-anders-button"
                  onClick={() => setShowAndersPage(true)}
                >
                  Wijzig vrije producten
                </button>
              </div>
            )}

            <div className="form-section">
              <label htmlFor="bestelling-notitie">
                <strong>Notitie</strong> <span className="optional-label">(optioneel)</span>
              </label>
              <textarea
                id="bestelling-notitie"
                className="bestelling-notitie-input"
                value={notitie}
                onChange={(e) => {
                  setSubmitError(null);
                  setNotitie(e.target.value);
                }}
                placeholder="Bijv. allergieën, speciale wensen of opmerkingen voor de kok..."
                rows={3}
                maxLength={500}
              />
              <small className="notitie-hint">{notitie.length}/500 tekens</small>
            </div>

            <div className="form-section form-actions-desktop">
              <button
                type="button"
                className="draft-button"
                onClick={handleSaveDraftClick}
                disabled={submitting || !!successMessage}
              >
                Concept opslaan
              </button>
              <button type="submit" className="submit-button" disabled={submitting || !!successMessage}>
                {submitting ? 'Bezig met plaatsen...' : 'Bestelling Plaatsen'}
              </button>
            </div>
          </form>
        </div>
      </main>

      <div className="bestelling-sticky-bar">
        {cartCount > 0 && (
          <span className="sticky-cart-count">{cartCount} in concept</span>
        )}
        <div className="sticky-bar-actions">
          <button
            type="button"
            className="draft-button"
            onClick={handleSaveDraftClick}
            disabled={submitting || !!successMessage}
          >
            Concept
          </button>
          <button
            type="submit"
            form="nieuwe-bestelling-form"
            className="submit-button"
            disabled={submitting || !!successMessage}
          >
            {submitting ? 'Plaatsen...' : 'Plaats bestelling'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NieuweBestellingPage;
